/**
 * @file CrmPagination.tsx
 * @description Постраничный вывод реестров зоны «Клиенты и сделки»
 * (phase19.31): «1–50 из 312» · ‹ «1 из 7» › · «По 50 ▾».
 *
 * Высоты кнопок — из темы (компактные 32 на широком экране, 48 на телефоне),
 * размер страницы — `SelectField`, не нативный список. Страница и размер
 * живут в адресе — компонент только сообщает, что выбрали. Одна страница
 * без выбора размера не нужна: при пустом списке компонент молчит.
 */

import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "../ui/button";
import { SelectField } from "../ui/SelectField";
import { CRM_PAGE_SIZES, listPageRange } from "../../lib/crm-list-query";

const SIZE_OPTIONS = CRM_PAGE_SIZES.map((size) => ({ value: String(size), label: `По ${size}` }));

interface Props {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Подпись навигации для скринридера: «Страницы реестра сделок». */
  label: string;
}

export function CrmPagination({ page, pageSize, total, onPageChange, onPageSizeChange, label }: Props) {
  const { from, to, pages } = listPageRange(page, pageSize, total);
  if (total === 0) return null;
  return (
    <nav aria-label={label} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <span className="type-body tabular-nums text-on-surface-variant">
        {from}–{to} из {total}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Предыдущая страница"
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="type-body min-w-12 text-center tabular-nums" aria-current="page">
          {page} из {pages}
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pages}
          aria-label="Следующая страница"
        >
          <ChevronRight className="size-4" />
        </Button>
        <SelectField
          ariaLabel="Записей на странице"
          value={String(pageSize)}
          onChange={(value) => onPageSizeChange(Number(value))}
          options={SIZE_OPTIONS}
          className="w-[120px]"
        />
      </div>
    </nav>
  );
}
